import { formatPesoAmount } from '../utils/money';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiEdit, FiEye, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { getProviderSummary, getProviders, restoreProvider, softDeleteProvider } from '../api';
import useCurrentUser from '../hooks/useCurrentUser';
import { hasPermission } from '../utils/permissions';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import useConfirmDialog from '../hooks/useConfirmDialog';
import { getProviderApiErrorMessage } from '../utils/provider';

const ProvidersListPage = () => {
  const navigate = useNavigate();
  const branchId = useParams().branchId || localStorage.getItem('selectedBranchId');
  const [providers, setProviders] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [totalProviders, setTotalProviders] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedProviders, setExpandedProviders] = useState(new Set());
  const { confirm } = useConfirmDialog();
  const { user } = useCurrentUser();
  const pageSize = 12;

  const canCreateProvider = hasPermission(user, 'provider.create');
  const canUpdateProvider = hasPermission(user, 'provider.update');
  const canDeleteProvider = hasPermission(user, 'provider.delete');

  const fetchSummaries = async (providerRows) => {
    if (!providerRows || providerRows.length === 0) {
      setSummaries({});
      return;
    }
    const results = await Promise.allSettled(
      providerRows.map((provider) => getProviderSummary(provider.id))
    );
    const summaryMap = {};
    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        summaryMap[providerRows[index].id] = result.value;
      }
    });
    setSummaries(summaryMap);
  };

  const fetchProviders = async () => {
    setLoading(true);
    setError('');
    try {
      const normalizedSearch = searchTerm.trim();
      const data = await getProviders({
        includeDeleted: showDeleted,
        page,
        pageSize,
        ...(normalizedSearch ? { search: normalizedSearch } : {}),
      });
      const normalized = normalizePaginatedResponse(data);
      setProviders(normalized.data || []);
      setTotalProviders(normalized.total || 0);
      setTotalPages(
        normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / pageSize))
      );
      await fetchSummaries(normalized.data || []);
    } catch (err) {
      console.error(err);
      setError(getProviderApiErrorMessage(err, 'Error al cargar proveedores.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProviders();
  }, [showDeleted, page, searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, showDeleted]);

  const handleSoftDelete = async (provider) => {
    if (!canDeleteProvider) return;
    const approved = await confirm({
      title: 'Eliminar proveedor',
      message: `Eliminar proveedor ${provider.name}?`,
      confirmLabel: 'Eliminar',
      cancelLabel: 'Cancelar',
      tone: 'danger',
    });
    if (!approved) return;
    try {
      await softDeleteProvider(provider.id);
      fetchProviders();
    } catch (err) {
      console.error(err);
      setError(getProviderApiErrorMessage(err, 'Error al eliminar proveedor.'));
    }
  };

  const handleRestore = async (provider) => {
    if (!canUpdateProvider) return;
    try {
      await restoreProvider(provider.id);
      fetchProviders();
    } catch (err) {
      console.error(err);
      setError(getProviderApiErrorMessage(err, 'Error al restaurar proveedor.'));
    }
  };

  const toggleProviderDetails = (providerId) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(providerId)) {
        next.delete(providerId);
      } else {
        next.add(providerId);
      }
      return next;
    });
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Proveedores</h1>
          <p className="text-sm text-gray-600">
            Administra datos de contacto, cuentas y saldos.
          </p>
        </div>
        {canCreateProvider && (
          <button
            onClick={() => navigate(`/branches/${branchId}/providers/new`)}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Nuevo Proveedor
          </button>
        )}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
        <input
          type="text"
          placeholder="Buscar por nombre, rut o giro..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg shadow focus:ring-2 focus:ring-blue-500"
        />
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Mostrar eliminados
          </label>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <span className="px-2 py-1 rounded bg-blue-100 text-blue-700">
              Total: {totalProviders}
            </span>
            <span className="px-2 py-1 rounded bg-emerald-100 text-emerald-700">
              En pagina: {providers.length}
            </span>
          </div>
        </div>
      </div>

      {error && <p className="text-red-600 mb-4">{error}</p>}

      {loading ? (
        <p className="text-gray-600">
          <InlineLoading />
        </p>
      ) : (
        <ul className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {providers.length === 0 ? (
            <li className="text-gray-600">No hay proveedores para mostrar.</li>
          ) : (
            providers.map((provider) => {
              const bankCount = provider.ProviderBankAccounts?.length || 0;
              const summary = summaries[provider.id];
              const balance = Number(summary?.balance || 0);
              const pending = Math.max(balance, 0);
              const credit = Math.max(Number(summary?.credit || 0), 0);
              const isExpanded = expandedProviders.has(provider.id);
              const detailsId = `provider-${provider.id}-details`;
              return (
                <li
                  key={provider.id}
                  className="border border-emerald-100 rounded-xl p-4 shadow-sm bg-gradient-to-br from-white via-white to-sky-50 hover:shadow-md transition-shadow"
                >
                  <button
                    type="button"
                    onClick={() => toggleProviderDetails(provider.id)}
                    className={`w-full text-left rounded-lg p-3 transition-colors ${
                      isExpanded ? 'bg-emerald-50/70' : 'bg-white/70'
                    }`}
                    aria-expanded={isExpanded}
                    aria-controls={detailsId}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{provider.name}</h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Rut: {provider.rut || '—'}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        {provider.isDeleted && (
                          <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                            Eliminado
                          </span>
                        )}
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {isExpanded ? 'Ocultar' : 'Ver mas'}
                        </span>
                      </div>
                    </div>
                  </button>

                  {isExpanded && (
                    <div id={detailsId} className="mt-3 space-y-2 text-sm text-gray-700">
                      <div className="flex flex-wrap items-center gap-2">
                        {provider.giro && (
                          <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                            Giro: {provider.giro}
                          </span>
                        )}
                        <span className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700">
                          Cuentas: {bankCount}
                        </span>
                      </div>
                      <p>Email: {provider.email || '—'}</p>
                      <p>Telefono: {provider.phone || '—'}</p>
                      <p>Direccion: {provider.address || '—'}</p>
                      <p>Ciudad: {provider.city || '—'}</p>
                      <p>
                        Saldo pendiente: {summary ? `$${formatPesoAmount(pending)}` : '—'}
                      </p>
                      {credit > 0 && (
                        <p className="text-emerald-700">
                          Credito: ${formatPesoAmount(credit)}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-emerald-100">
                    <button
                      onClick={() => navigate(`/branches/${branchId}/providers/${provider.id}`)}
                      className="bg-emerald-500 text-white px-4 py-2 rounded hover:bg-emerald-600 flex items-center gap-2"
                    >
                      <FiEye />
                      Ver detalle
                    </button>
                    {canUpdateProvider && (
                      <button
                        onClick={() => navigate(`/branches/${branchId}/providers/${provider.id}/edit`)}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 flex items-center gap-2"
                      >
                        <FiEdit />
                        Editar
                      </button>
                    )}
                    {provider.isDeleted ? (
                      canUpdateProvider && (
                        <button
                          onClick={() => handleRestore(provider)}
                          className="bg-amber-500 text-white px-4 py-2 rounded hover:bg-amber-600 flex items-center gap-2"
                        >
                          <FiRefreshCw />
                          Restaurar
                        </button>
                      )
                    ) : (
                      canDeleteProvider && (
                        <button
                          onClick={() => handleSoftDelete(provider)}
                          className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 flex items-center gap-2"
                        >
                          <FiTrash2 />
                          Eliminar
                        </button>
                      )
                    )}
                  </div>
                </li>
              );
            })
          )}
        </ul>
      )}

      {totalProviders > pageSize && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-6">
          <div>
            Mostrando {totalProviders === 0 ? 0 : (page - 1) * pageSize + 1}-
            {Math.min(page * pageSize, totalProviders)} de {totalProviders}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Anterior
            </button>
            <span>
              {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={page >= totalPages}
              className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProvidersListPage;
